import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import api from "../api/api";

function emptyValueByType(type) {
  switch (type) {
    case "checkbox":
      return false;
    case "number":
      return "";
    case "file":
      return null;
    case "date":
      return new Date().toISOString().slice(0, 10);
    default:
      return "";
  }
}

function buildInitialValues(schema) {
  const values = {};
  (schema?.fields || []).forEach((field) => {
    if (field.type === "repeatable_group") {
      values[field.key] = [];
    } else {
      values[field.key] = emptyValueByType(field.type);
    }
  });
  return values;
}

function buildEmptyGroupItem(fields = []) {
  const item = {};
  fields.forEach((field) => {
    item[field.key] = emptyValueByType(field.type);
  });
  return item;
}

function parentDisplayLabel(parent) {
  if (!parent) return "";
  const type = parent.type_parent ? `${parent.type_parent} - ` : "";
  const date = parent.date_naissance ? ` - ne(e) le ${parent.date_naissance}` : "";
  return `${type}${parent.nom || ""} ${parent.prenom || ""}${date}`.trim();
}

function childDisplayLabel(child) {
  if (!child) return "";
  const details = [
    child.date_naissance ? `ne(e) le ${child.date_naissance}` : null,
    child.classe,
    child.etablissement,
  ].filter(Boolean).join(" - ");
  return `${child.nom || ""} ${child.prenom || ""}${details ? ` (${details})` : ""}`.trim();
}

export default function DynamicForm({
  schema,
  onSubmit = null,
  loading = false,
  error = null,
}) {
  const [values, setValues] = useState(() => buildInitialValues(schema));
  const [files, setFiles] = useState({});
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    setValues(buildInitialValues(schema));
    setFiles({});
  }, [schema]);

  useEffect(() => {
    let mounted = true;

    const fetchProfile = async () => {
      try {
        setProfileLoading(true);
        const res = await api.get("adherents/profile/");
        if (mounted) setProfile(res.data);
      } catch (e) {
        if (mounted) setProfile(null);
      } finally {
        if (mounted) setProfileLoading(false);
      }
    };

    fetchProfile();

    return () => {
      mounted = false;
    };
  }, []);

  const repeatableFields = useMemo(
    () => (schema?.fields || []).filter((f) => f.type === "repeatable_group"),
    [schema]
  );

  useEffect(() => {
    repeatableFields.forEach((field) => {
      const countFrom = field.count_from;
      if (!countFrom) return;

      const rawCount = values[countFrom];
      const count = Math.max(0, parseInt(rawCount || 0, 10) || 0);
      const current = Array.isArray(values[field.key]) ? values[field.key] : [];
      const groupFields = field.fields || [];

      if (current.length === count) return;

      const next = [...current];

      while (next.length < count) {
        next.push(buildEmptyGroupItem(groupFields));
      }

      while (next.length > count) {
        next.pop();
      }

      setValues((prev) => ({
        ...prev,
        [field.key]: next,
      }));
    });
  }, [repeatableFields, values]);

  const handleChange = (key, value) => {
    setValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFileChange = (key, file) => {
    setFiles((prev) => ({
      ...prev,
      [key]: file || null,
    }));
  };

  const handleGroupChange = (groupKey, index, fieldKey, value) => {
    setValues((prev) => {
      const current = Array.isArray(prev[groupKey]) ? [...prev[groupKey]] : [];
      current[index] = {
        ...(current[index] || {}),
        [fieldKey]: value,
      };

      return {
        ...prev,
        [groupKey]: current,
      };
    });
  };

  const renderSpecialSelect = (field) => {
    const value = values[field.key] ?? "";

    if (profileLoading) {
      return (
        <div className="d-flex align-items-center gap-2">
          <Spinner size="sm" animation="border" />
          <span>Chargement des données…</span>
        </div>
      );
    }

    if (field.type === "adherent_child_select") {
      const enfants = Array.isArray(profile?.enfants) ? profile.enfants : [];
      return (
        <Form.Select
          value={value}
          required={field.required}
          onChange={(e) => handleChange(field.key, e.target.value)}
        >
          <option value="">-- Choisir un enfant --</option>
          {enfants.map((child) => (
            <option key={child.id} value={child.id}>
              {childDisplayLabel(child)}
            </option>
          ))}
        </Form.Select>
      );
    }

    if (field.type === "adherent_parent_select") {
      const parents = Array.isArray(profile?.parents) ? profile.parents : [];
      return (
        <Form.Select
          value={value}
          required={field.required}
          onChange={(e) => handleChange(field.key, e.target.value)}
        >
          <option value="">-- Choisir un parent --</option>
          {parents.map((parent) => (
            <option key={parent.id} value={parent.id}>
              {parentDisplayLabel(parent)}
            </option>
          ))}
        </Form.Select>
      );
    }

    return null;
  };

  const renderSimpleField = (field) => {
    const value = values[field.key];

    if (
      field.type === "adherent_child_select" ||
      field.type === "adherent_parent_select"
    ) {
      return renderSpecialSelect(field);
    }

    switch (field.type) {
      case "text":
        return (
          <Form.Control
            type="text"
            value={value ?? ""}
            required={field.required}
            onChange={(e) => handleChange(field.key, e.target.value)}
          />
        );

      case "date":
        return (
          <Form.Control
            type="date"
            value={value || new Date().toISOString().slice(0, 10)}
            readOnly
          />
        );

      case "number":
        return (
          <Form.Control
            type="number"
            value={value ?? ""}
            required={field.required}
            onChange={(e) => handleChange(field.key, e.target.value)}
          />
        );

      case "textarea":
        return (
          <Form.Control
            as="textarea"
            rows={3}
            value={value ?? ""}
            required={field.required}
            onChange={(e) => handleChange(field.key, e.target.value)}
          />
        );

      case "select":
        return (
          <Form.Select
            value={value ?? ""}
            required={field.required}
            onChange={(e) => handleChange(field.key, e.target.value)}
          >
            <option value="">-- Choisir --</option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        );

      case "radio":
        return (
          <div>
            {field.options?.map((opt) => (
              <Form.Check
                key={opt.value}
                type="radio"
                name={field.key}
                id={`${field.key}_${opt.value}`}
                label={opt.label}
                value={opt.value}
                checked={value === opt.value}
                onChange={(e) => handleChange(field.key, e.target.value)}
              />
            ))}
          </div>
        );

      case "checkbox":
        return (
          <Form.Check
            type="checkbox"
            label={field.label}
            checked={Boolean(value)}
            onChange={(e) => handleChange(field.key, e.target.checked)}
          />
        );

      case "file":
        return (
          <Form.Control
            type="file"
            required={field.required}
            onChange={(e) => handleFileChange(field.key, e.target.files?.[0] || null)}
          />
        );

      default:
        return null;
    }
  };

  const renderRepeatableGroup = (field) => {
    const items = Array.isArray(values[field.key]) ? values[field.key] : [];
    const groupFields = field.fields || [];

    return (
      <div>
        {items.length === 0 ? (
          <Alert variant="secondary" className="mb-0">
            Aucun élément à remplir pour le moment.
          </Alert>
        ) : (
          items.map((item, index) => (
            <Card key={`${field.key}_${index}`} className="mb-3">
              <Card.Body>
                <div className="fw-bold mb-3">
                  {field.label || field.key} #{index + 1}
                </div>

                <Row className="g-3">
                  {groupFields.map((child) => (
                    <Col md={6} key={`${field.key}_${index}_${child.key}`}>
                      <Form.Group>
                        <Form.Label>{child.label}</Form.Label>

                        {child.type === "textarea" ? (
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={item?.[child.key] ?? ""}
                            required={child.required}
                            onChange={(e) =>
                              handleGroupChange(field.key, index, child.key, e.target.value)
                            }
                          />
                        ) : child.type === "select" ? (
                          <Form.Select
                            value={item?.[child.key] ?? ""}
                            required={child.required}
                            onChange={(e) =>
                              handleGroupChange(field.key, index, child.key, e.target.value)
                            }
                          >
                            <option value="">-- Choisir --</option>
                            {child.options?.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Form.Select>
                        ) : child.type === "radio" ? (
                          <div>
                            {child.options?.map((opt) => (
                              <Form.Check
                                key={opt.value}
                                type="radio"
                                name={`${field.key}_${index}_${child.key}`}
                                id={`${field.key}_${index}_${child.key}_${opt.value}`}
                                label={opt.label}
                                value={opt.value}
                                checked={item?.[child.key] === opt.value}
                                onChange={(e) =>
                                  handleGroupChange(field.key, index, child.key, e.target.value)
                                }
                              />
                            ))}
                          </div>
                        ) : child.type === "checkbox" ? (
                          <Form.Check
                            type="checkbox"
                            checked={Boolean(item?.[child.key])}
                            onChange={(e) =>
                              handleGroupChange(field.key, index, child.key, e.target.checked)
                            }
                          />
                        ) : (
                          <Form.Control
                            type={child.type === "number" ? "number" : child.type || "text"}
                            value={item?.[child.key] ?? ""}
                            required={child.required}
                            onChange={(e) =>
                              handleGroupChange(field.key, index, child.key, e.target.value)
                            }
                          />
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          ))
        )}
      </div>
    );
  };

  const canSubmit = typeof onSubmit === "function";

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();

        if (!canSubmit) {          return;
        }

        onSubmit(values, files);
      }}
    >
      {error ? (
        <Alert variant="danger">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </Alert>
      ) : null}

      <Alert variant="success" className="mb-3">
        <strong>Vos informations adherent sont deja ajoutees automatiquement.</strong>
        <div>
          {profileLoading
            ? "Chargement du profil..."
            : profile
            ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || profile.matricule
            : "Votre compte connecte sera utilise pour cette demande."}
          {profile?.matricule ? ` - Matricule ${profile.matricule}` : ""}
        </div>
        <small>
          Remplissez seulement les informations additionnelles demandees pour cet engagement.
        </small>
      </Alert>

      {(schema?.fields || []).map((field) => (
        <Form.Group key={field.key} className="mb-3">
          {field.type !== "checkbox" && (
            <Form.Label>
              {field.label}
              {field.required ? " *" : ""}
            </Form.Label>
          )}

          {field.type === "repeatable_group"
            ? renderRepeatableGroup(field)
            : renderSimpleField(field)}
        </Form.Group>
      ))}

      <Button type="submit" disabled={loading || !canSubmit}>
        {loading ? "Envoi..." : "Soumettre"}
      </Button>

      {!canSubmit && (
        <Alert variant="warning" className="mt-3 mb-0">
          Ce formulaire est affiché en mode aperçu uniquement.
        </Alert>
      )}
    </Form>
  );
}
